import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  TouchableOpacity,
  TextInput,
  RefreshControl,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import Svg, { Path } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import { searchApi, SearchResults } from '../../services/apiService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import Navbar from '../../components/Navbar';

// ============================================================
// SVG Icon Components
// ============================================================

const SearchIcon = ({ size = 20, color = COLORS.textLight }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M21 21L16.65 16.65" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ChevronRightIcon = ({ size = 20, color = COLORS.textLight }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M9 18L15 12L9 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

type RootStackParamList = {
  SearchResults: { query?: string };
};

type SearchResultsScreenRouteProp = RouteProp<RootStackParamList, 'SearchResults'>;

// ============================================================
// SearchResultsScreen Component
// ============================================================

export default function SearchResultsScreen() {
  const navigation = useNavigation();
  const route = useRoute<SearchResultsScreenRouteProp>();
  const { t, isRTL } = useLanguage();
  const { colors } = useTheme();

  const [searchQuery, setSearchQuery] = useState(route.params?.query || '');
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Perform search
  const performSearch = async (query: string) => {
    if (!query || query.length < 2) {
      setError(t('search.minTwoChars'));
      setResults(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await searchApi.search(query);
      setResults(data);
    } catch (err: any) {
      console.error('Search error:', err);
      setError(err.response?.data?.error || t('search.error'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Search on mount if query provided
  useEffect(() => {
    if (route.params?.query) {
      performSearch(route.params.query);
    }
  }, [route.params?.query]);

  // Handle search submit
  const handleSearch = () => {
    performSearch(searchQuery);
  };

  // Handle refresh
  const onRefresh = () => {
    setRefreshing(true);
    performSearch(searchQuery);
  };

  // Navigation handlers for different entity types
  const navigateToGoal = (id: number) => {
    navigation.navigate('GoalDetail' as never, { id } as never);
  };

  const navigateToInitiative = (id: number) => {
    navigation.navigate('InitiativeDetail' as never, { id } as never);
  };

  const navigateToActivity = (id: number) => {
    navigation.navigate('ActivityDetail' as never, { id } as never);
  };

  const navigateToKPI = (id: number) => {
    navigation.navigate('KPIDetail' as never, { id } as never);
  };

  const navigateToProcess = (id: number) => {
    navigation.navigate('ProcessDetail' as never, { id } as never);
  };

  const navigateToSubProcess = (id: number) => {
    navigation.navigate('SubProcessDetail' as never, { id } as never);
  };

  const navigateToService = (id: number) => {
    navigation.navigate('ServiceDetail' as never, { id } as never);
  };

  const navigateToUnitGoal = (id: number) => {
    navigation.navigate('UnitGoalDetail' as never, { id } as never);
  };

  const navigateToRisk = (id: number) => {
    // TODO: Implement when RiskDetailScreen is created
    console.log('Navigate to Risk:', id);
    // navigation.navigate('RiskDetail' as never, { id } as never);
  };

  // Render result item
  const renderResultItem = (
    item: any,
    onPress: () => void,
    subtitle?: string
  ) => (
    <TouchableOpacity
      key={item.id}
      style={[styles.resultItem, { backgroundColor: colors.card, borderColor: colors.cardBorder }]}
      onPress={onPress}
    >
      <View style={styles.resultContent}>
        <Text style={[styles.resultTitle, { color: colors.text }]} numberOfLines={2}>
          {item.name}
        </Text>
        {item.description && (
          <Text style={[styles.resultDescription, { color: colors.textSecondary }]} numberOfLines={2}>
            {item.description}
          </Text>
        )}
        {subtitle && (
          <Text style={[styles.resultSubtitle, { color: colors.textLight }]} numberOfLines={1}>
            {subtitle}
          </Text>
        )}
        <Text style={[styles.resultOrg, { color: colors.textLight }]} numberOfLines={1}>
          {item.organization__name}
        </Text>
      </View>
      <ChevronRightIcon size={20} color={colors.textLight} />
    </TouchableOpacity>
  );

  // Render category section
  const renderCategory = (
    titleKey: string,
    count: number,
    items: any[],
    onItemPress: (id: number) => void,
    getSubtitle?: (item: any) => string
  ) => {
    if (count === 0) return null;

    return (
      <View style={styles.categorySection}>
        <Text style={[styles.categoryTitle, { color: colors.text }]}>
          {t(titleKey)} ({count})
        </Text>
        {items.map((item) =>
          renderResultItem(
            item,
            () => onItemPress(item.id),
            getSubtitle ? getSubtitle(item) : undefined
          )
        )}
      </View>
    );
  };

  // Dynamic theme styles
  const themedStyles = {
    container: {
      backgroundColor: colors.background,
    },
    searchWrapper: {
      backgroundColor: colors.card,
      borderBottomColor: colors.cardBorder,
    },
    searchInput: {
      backgroundColor: colors.gray50,
      borderColor: colors.gray200,
      color: colors.text,
    },
    emptyText: {
      color: colors.textSecondary,
    },
    errorText: {
      color: COLORS.danger,
    },
  };

  return (
    <View style={[styles.container, themedStyles.container]}>
      <Navbar title={t('search.searchResults')} showSearch={false} />

      {/* Search Input */}
      <View style={[styles.searchWrapper, themedStyles.searchWrapper]}>
        <View style={[styles.searchInputWrapper, themedStyles.searchInput]}>
          <TextInput
            style={[styles.searchInput, { color: colors.text }]}
            placeholder={t('dashboard.searchPlaceholder')}
            placeholderTextColor={colors.textLight}
            value={searchQuery}
            onChangeText={setSearchQuery}
            onSubmitEditing={handleSearch}
            autoFocus={!route.params?.query}
          />
          <TouchableOpacity onPress={handleSearch}>
            <SearchIcon size={20} color={colors.textLight} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Results */}
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={COLORS.primary} />
        }
      >
        {loading && !refreshing && (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={COLORS.primary} />
          </View>
        )}

        {error && (
          <View style={styles.centerContainer}>
            <Text style={[styles.errorText, themedStyles.errorText]}>{error}</Text>
          </View>
        )}

        {!loading && !error && !results && (
          <View style={styles.centerContainer}>
            <SearchIcon size={48} color={colors.textLight} />
            <Text style={[styles.emptyText, themedStyles.emptyText]}>
              {t('search.enterQuery')}
            </Text>
          </View>
        )}

        {!loading && results && results.total_count === 0 && (
          <View style={styles.centerContainer}>
            <SearchIcon size={48} color={colors.textLight} />
            <Text style={[styles.emptyText, themedStyles.emptyText]}>
              {t('search.noResults')}
            </Text>
          </View>
        )}

        {!loading && results && results.total_count > 0 && (
          <View style={styles.resultsContainer}>
            <Text style={[styles.totalCount, { color: colors.textSecondary }]}>
              {t('search.foundResults', { count: results.total_count })}
            </Text>

            {renderCategory(
              'goals.strategicGoals',
              results.results.strategic_goals.count,
              results.results.strategic_goals.items,
              navigateToGoal
            )}

            {renderCategory(
              'initiatives.title',
              results.results.initiatives.count,
              results.results.initiatives.items,
              navigateToInitiative,
              (item) => item.strategic_goal__name
            )}

            {renderCategory(
              'initiatives.activities',
              results.results.activities.count,
              results.results.activities.items,
              navigateToActivity,
              (item) => item.initiative__name
            )}

            {renderCategory(
              'kpis.title',
              results.results.kpis.count,
              results.results.kpis.items,
              navigateToKPI,
              (item) => t(`kpis.sourceType.${item.source_type}`)
            )}

            {renderCategory(
              'processes.title',
              results.results.processes.count,
              results.results.processes.items,
              navigateToProcess
            )}

            {renderCategory(
              'processes.subProcesses',
              results.results.sub_processes.count,
              results.results.sub_processes.items,
              navigateToSubProcess,
              (item) => item.process__name
            )}

            {renderCategory(
              'services.title',
              results.results.services.count,
              results.results.services.items,
              navigateToService
            )}

            {renderCategory(
              'unitGoals.title',
              results.results.unit_goals.count,
              results.results.unit_goals.items,
              navigateToUnitGoal,
              (item) => item.unit__name
            )}

            {renderCategory(
              'risks.title',
              results.results.risks.count,
              results.results.risks.items,
              navigateToRisk,
              (item) => t(`risks.level.${item.level}`)
            )}
          </View>
        )}
      </ScrollView>
    </View>
  );
}

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.background,
  },
  searchWrapper: {
    backgroundColor: COLORS.white,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.gray200,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  searchInputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.gray50,
    borderWidth: 1,
    borderColor: COLORS.gray200,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.gray700,
    textAlign: 'right',
    paddingRight: 8,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: COLORS.textLight,
    textAlign: 'center',
    marginTop: 16,
  },
  errorText: {
    fontSize: 16,
    fontFamily: FONTS.regular,
    color: COLORS.danger,
    textAlign: 'center',
  },
  resultsContainer: {
    padding: 16,
  },
  totalCount: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: COLORS.textLight,
    marginBottom: 16,
    textAlign: 'right',
  },
  categorySection: {
    marginBottom: 24,
  },
  categoryTitle: {
    fontSize: 16,
    fontFamily: FONTS.bold,
    color: COLORS.gray900,
    marginBottom: 12,
    textAlign: 'right',
  },
  resultItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.gray200,
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  resultContent: {
    flex: 1,
    paddingRight: 12,
  },
  resultTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: COLORS.gray900,
    marginBottom: 4,
    textAlign: 'right',
  },
  resultDescription: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: COLORS.textLight,
    marginBottom: 4,
    textAlign: 'right',
  },
  resultSubtitle: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: COLORS.gray500,
    marginBottom: 4,
    textAlign: 'right',
  },
  resultOrg: {
    fontSize: 11,
    fontFamily: FONTS.regular,
    color: COLORS.gray400,
    textAlign: 'right',
  },
});
